import { getOktaWidgetModel } from '../../utils/widget-facade';
import { isAnetUser } from '../../utils/user-utils';
import AmplitudeUtils from '../../utils/amplitude-utils';
import { ERROR_KEYS, ERROR_MESSAGES } from '../../constants';
import { createWrapper } from '../../utils/react-utils';
import WidgetFormField from '../../components/common/WidgetFormField';
import { ReadOnlyInput } from '@athena/forge';
import { getLocalizedString } from '../../utils/i18n-utils';
import TermsContainer from '../../components/password-reset/TermsContainer';
import PasswordResetBaseCustomizer from './base';
import { setTermsAndConditionsShownCookie } from '../../utils/storage-utils';

export default class PasswordResetNonAnetCustomizer extends PasswordResetBaseCustomizer {
  headerText = this.getTitle();
  headerDescription = settings.showSetPasswordDescription
    ? getLocalizedString('password.create.setpassword.description')
    : '';

  private getTitle() {
    if (settings.newUser) {
      return getLocalizedString('password.create.heading');
    }
    return getLocalizedString('password.edit.heading');
  }

  protected customizePasswordResetButton() {
    // Use base class validation wrapper which includes password length check
    this.wrapValidationWithPasswordLengthCheck();
    this.onPasswordReset();
  }

  protected onPasswordReset() {
    const {
      oktaSignIn: { authClient },
    } = this;
    const originalProceed = authClient.idx.proceed.bind(authClient);

    // Widget calls this method after performing frontend validation with the expected payload
    // See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L284
    authClient.idx.proceed = async (...args: any) => {
      const model = getOktaWidgetModel();
      //Handle errors by listing to error events once the button is clicked. This helps in receiving a more "normalized"
      //error payload - See https://github.com/okta/okta-signin-widget/blob/7.8/src/v2/controllers/FormController.ts#L385
      model.once('error', (_: any, error: any) => {
        const errorMessage =
          error?.responseJSON?.errorCauses?.[0]?.errorSummary ??
          error?.responseJSON?.xhr?.responseJSON?.errorSummary ??
          error?.errorSummary ??
          ERROR_MESSAGES[ERROR_KEYS.GENERIC_INTERNAL_ERROR];
        AmplitudeUtils.logPasswordResetEvent('PasswordChangeFailed', {
          errorMessage,
        });
      });

      AmplitudeUtils.logPasswordResetEvent('PasswordChangeInitiated', {});
      const { step } = args[0];
      if (step === 'reenroll-authenticator') {
        const res = await originalProceed.apply(authClient, args);
        if (!res?.context?.messages?.value?.length) {
          settings.newUser && setTermsAndConditionsShownCookie();
          AmplitudeUtils.logPasswordResetEvent('PasswordChangeSuccess', {});
        }
        return res;
      }
      return originalProceed.apply(authClient, args);
    };
  }

  async shouldCustomize() {
    return (await super.shouldCustomize()) && !isAnetUser();
  }
  async customize() {
    await super.customize();
    this.addTermsInfo();
    jQueryCourage('.o-form-content').css('padding-bottom', '0px');
    this.shouldEmailFieldBeAdded() && this.addEmailField();
  }

  private shouldEmailFieldBeAdded() {
    return settings.username && settings.newUser;
  }

  private addEmailField() {
    const primaryAuthForm = jQueryCourage('[data-se="o-form-fieldset-credentials.passcode"]');
    const additionalLinksWrapper = createWrapper({ hidden: false });
    primaryAuthForm.after(additionalLinksWrapper);
    this.renderReactComponent({
      id: 'athena-email-field',
      container: additionalLinksWrapper,
      component: (
        <WidgetFormField
          id="athena-email-field-id"
          labelText={getLocalizedString('account.demographics.email')}
          inputAs={ReadOnlyInput}
          text={settings.username}
          className="fe_u_margin--bottom-medium"
        />
      ),
    });
  }

  private addTermsInfo() {
    const primaryAuthForm = jQueryCourage('.o-form-content');
    const additionalLinksWrapper = createWrapper({ hidden: false });
    primaryAuthForm.append(additionalLinksWrapper);
    this.renderReactComponent({
      id: 'athena-terms-container',
      container: additionalLinksWrapper,
      component: <TermsContainer />,
    });
  }

}
